// @flow
import { windowWrapper } from '@clubmed/utils';
import axios from 'axios';
import { isEmpty, last } from 'lodash';
import cookie from 'js-cookie';

import config from 'Config';
import { invariant } from 'Utils/invariant/invariant';
import { localStorageAvailable } from 'Client/local-storage-api';
import { CHATBOT_EVENTS, trackChatbotEvent } from './Chatbot.tracking';

const CHATBOT_AVATAR_PATH = '/medias/gentil-bot.svg';
const CHATBOT_LOCAL_STORAGE_KEY = 'BOTFUEL_WEBCHAT_USER_ID';

const { appToken, apiUrl } = config.get('botfuel');

// localStorage API compatible with Botfuel webchat. For the Chatbot only.
function setInLocalStorage(key, value) {
  if (localStorageAvailable()) {
    localStorage.setItem(key, value);
  }
}

function getFromLocalStorage(key) {
  if (localStorageAvailable()) {
    return localStorage.getItem(key);
  }
}

export function setUserId(googleTrackingId: ?string) {
  return new Promise(resolve => {
    if (googleTrackingId) {
      resolve(googleTrackingId);
    } else {
      const resolveTimestampedId = () => resolve(`NO.ID.${Date.now()}`);
      const requestAnimationFrame = windowWrapper.get('requestAnimationFrame');
      if (!requestAnimationFrame) return resolveTimestampedId();
      let start = null;
      const getGaId = timestamp => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const gaId = cookie.get('_ga');
        if (gaId) {
          resolve(gaId);
        } else if (progress < 3000) {
          // Try fetching the _ga cookie at next repaint during 3s max.
          // This delays the chatbot initialization but ensures
          // more users are properly identified by their GA ID.
          requestAnimationFrame(getGaId);
        } else {
          // Or use a timestamp ID
          resolveTimestampedId();
        }
      };
      requestAnimationFrame(getGaId);
    }
  }).then(userId => {
    setInLocalStorage(CHATBOT_LOCAL_STORAGE_KEY, userId);
    return true;
  });
}

type SendAppContextProps = {
  pageContext: PageContext,
  deviceCategory: string,
  location: RouterLocation<UrlParams, QueryParams>,
};
function sendAppContext({ pageContext, deviceCategory, location }: SendAppContextProps) {
  const { origin } = location;
  const { pageId, pageType, pageMeta, pageTitle, pageDescription } = pageContext;
  const user = getFromLocalStorage(CHATBOT_LOCAL_STORAGE_KEY);
  axios.post(
    apiUrl,
    {
      user,
      deviceCategory,
      pageId,
      pageType,
      pageMeta,
      pageTitle,
      pageDescription,
      origin,
    },
    {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    },
  );
}

export function createChatbot() {
  const botConfig = {
    appToken: appToken,
    // extraAllowedOrigins: ['http://localhost:3000'],
    startOpen: false,
    startFullScreen: false,
    size: { width: 400, height: 600 },
    labels: {
      sendButtonLabel: 'Envoyer',
      messageInputPlaceholder: 'Ecrivez votre message ici',
      webchatHeaderTitle: 'GentilBot ClubMed',
      helpMessage: 'Les services et activités de nos destinations 😀',
    },
    theme: {
      layout: { rounded: 0 },
      buttons: { close: true, fullScreen: true },
      images: {
        botAvatar: `${windowWrapper.getOrigin()}${CHATBOT_AVATAR_PATH}`,
        startButton: `${windowWrapper.getOrigin()}${CHATBOT_AVATAR_PATH}`,
      },
      startButtonStyle: 'bubble',
      dialogStyle: 'hover',
      fixed: true,
    },
    menuActions: [
      {
        label: "Demander de l'aide",
        message: {
          type: 'text',
          payload: {
            value: 'Aide',
          },
        },
      },
      {
        label: 'Commencer une nouvelle conversation',
        message: {
          type: 'text',
          payload: {
            value: 'Recommencer',
          },
        },
      },
    ],
    parseHTML: true,
    sanitizeDOM: false,
    voiceEnabled: false,
  };
  const botfuelWebChat = windowWrapper.get('BotfuelWebChat');
  if (botfuelWebChat) {
    botfuelWebChat.init(botConfig);
  }

  return !!botfuelWebChat;
}

export const createFreespeeObserver = () => {
  return new MutationObserver(mutations => {
    const chatbotMessageMutations = mutations.filter(mutation => {
      const { addedNodes } = mutation;
      // $FlowFixMe stupid typings
      return !isEmpty(addedNodes) && Object.values(last(addedNodes).classList).includes('bf-bot');
    });

    // If there are between 1 and 3 new chatbot messages (excludes mutations from past conversation)
    if (!isEmpty(chatbotMessageMutations) && chatbotMessageMutations.length < 3) {
      const { addedNodes: chatbotMessageNodes } = last(chatbotMessageMutations);
      // $FlowFixMe stupid typings
      const chatbotMessageNode = last(chatbotMessageNodes);

      if (chatbotMessageNode.innerHTML.includes('<span class="freespee-schedule-call">')) {
        const scheduleCallNode = document.getElementById('scheduleCall');
        scheduleCallNode && scheduleCallNode.click();
      }
    }
  });
};

export const createBotFuelObserver = (callback: () => void) => {
  const targetNode = document.querySelector('body');
  const observerOptions = {
    childList: true,
    attributes: false,
    subtree: true,
  };

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      // ChatbotFuel really initialized
      if (mutation.type === 'childList' && mutation.target.id === 'botfuel') {
        callback();
      }
    });
  });

  observer.observe(targetNode, observerOptions);

  setTimeout(() => {
    observer.disconnect();
  }, 5000);
};

function observeFreespee(freespeeObserver: MutationObserver) {
  const observedNode = document.querySelector('.bf-message-list');
  freespeeObserver.observe(observedNode, {
    attributes: false,
    characterData: false,
    childList: true,
    subtree: false,
  });
}

function disconnectFreespee(freespeeObserver: MutationObserver) {
  freespeeObserver.disconnect();
}

export type ObserverCell = { current: null | MutationObserver };
export type SendAppContextCell = { current: null | (() => void) };
export type TrackingCell = {
  open: null | (() => void),
  close: null | (() => void),
};

type SetUpClickListenersParams = {
  pageContext: PageContext,
  appContext: AppContext,
  deviceCategory: string,
  observerCell: ObserverCell,
  sendAppContextCell: SendAppContextCell,
  trackingCell: TrackingCell,
  location: RouterLocation<UrlParams, QueryParams>,
};
// We send the pageId when the user clicks/sends a message. This means that we need recreate the
// event listeners whenever the page url changes
export const setUpClickListeners = ({
  sendAppContextCell,
  observerCell,
  trackingCell,
  appContext,
  pageContext,
  deviceCategory,
  location,
}: SetUpClickListenersParams) => {
  const startButton = document.querySelector('.bf-webchat-start-button');
  const closeButton = document.querySelector('.bf-webchat-close-button');
  if (startButton && closeButton) {
    // If listeners already exists (from a previous page), recreate them
    if (sendAppContextCell.current) {
      startButton.removeEventListener('click', sendAppContextCell.current);
    }
    if (trackingCell.open) {
      startButton.removeEventListener('click', trackingCell.open);
    }
    if (trackingCell.close) {
      closeButton.removeEventListener('click', trackingCell.close);
    }

    // Setup appContext click listeners
    const send = () => sendAppContext({ pageContext, deviceCategory, location });
    startButton.addEventListener('click', send);
    sendAppContextCell.current = send;

    // Setup Freespee observer click listeners
    const observer = observerCell.current;
    invariant(observer, 'The MutationObserver should always be defined');
    startButton.addEventListener('click', () => observeFreespee(observer));
    closeButton.addEventListener('click', () => disconnectFreespee(observer));

    // Setup Chatbot Tracking click listeners
    const trackOpen = () =>
      trackChatbotEvent(CHATBOT_EVENTS.CLICKED_OPEN, appContext, pageContext, location);
    startButton.addEventListener('click', trackOpen);
    trackingCell.open = trackOpen;
    const trackClose = () =>
      trackChatbotEvent(CHATBOT_EVENTS.CLICKED_CLOSE, appContext, pageContext, location);
    closeButton.addEventListener('click', trackClose);
    trackingCell.close = trackClose;

    const startButtonChid = startButton && startButton.firstChild;
    startButtonChid && startButtonChid.click();
  }
};
